'use client';
import {createIntercomSession as CIS, getOrders} from '@/services/user';
import useSession from '@/stores/session/useSession';
import Intercom, {boot, shutdown} from '@intercom/messenger-js-sdk';
import {useLocale} from 'next-intl';
import {useEffect, useRef, useState} from 'react';
import {usePathname} from 'next/navigation';

const INTERCOM_APP_ID = 'o8lwzkwc';
const hideHelpCenterRoutes: RegExp[] = [
  /^\/[a-z]{2}\/otto$/,
];

export default function IntercomComponent() {
  const pathname = usePathname();
  const session = useSession();
  const locale = useLocale();
  const [sessionExists, setSessionExists] = useState(!!session.token);
  const DEFAULT_INTERCOM_SETTINGS = {app_id: INTERCOM_APP_ID, language_override: locale};
  const shouldHideHelpCenter = useRef<boolean>(hideHelpCenterRoutes.some((regex) => regex.test(pathname)));

  const getLastOrderId = async () => {
    if (!session.token) return undefined;
    const responses = await Promise.all(
      ['active', 'completed'].map((status) => getOrders({page: 1, status})),
    );
    const orders = responses
      .filter((r) => r.ok)
      .flatMap((r) => r.data?.items ?? []);
    if (!orders.length) return undefined;

    const lastOrder = orders.reduce((latest, order) =>
      new Date(order.date ?? 0) > new Date(latest.date ?? 0) ? order : latest,
    );
    return `${lastOrder.hashid || lastOrder._id}`;
  };

  const createLoggedInUserSession = async () => {
    const [res, lastOrderId] = await Promise.all([CIS(), getLastOrderId()]);
    console.log('Creating Intercom session with logged in user', res);
    if (!res.ok) return;
    shutdown();
    boot({
      ...DEFAULT_INTERCOM_SETTINGS,
      intercom_user_jwt: res.data.token,
      user_id: session.user?._id,
      email: session.user?.email,
      name: `${session.user?.name} ${session.user?.lastname}`,
      phone: session.user?.phone,
      last_order_id: lastOrderId
    });
    setSessionExists(true);
  };

  const createSession = (rebootAfterHidden: boolean = false) => {
    if (!session.token) {
      console.log('Anonymous user');
      if (sessionExists || rebootAfterHidden) {
        console.log('Rebooting Intercom anonymously');
        shutdown();
        boot(DEFAULT_INTERCOM_SETTINGS);
      }
      setSessionExists(false);
      return;
    }

    // Necessario perchè a login non aggiornava il componente: CIS() non trovava il token
    return setTimeout(async () => {
      await createLoggedInUserSession();
    }, 300);
  }

  useEffect(() => {
    Intercom(DEFAULT_INTERCOM_SETTINGS);
    return () => shutdown();
  }, []);

  useEffect(() => {
    const timer = createSession();

    return () => clearTimeout(timer);
  }, [session]);

  useEffect(() => {
    const nowShouldHide = hideHelpCenterRoutes.some((regex) => regex.test(pathname));
    if (shouldHideHelpCenter.current && !nowShouldHide) {
      createSession(true);
    } else if (!shouldHideHelpCenter.current && nowShouldHide) {
      shutdown();
    }
    shouldHideHelpCenter.current = nowShouldHide;
  }, [pathname]);

  return <></>;
}
